import { useState, useCallback } from 'react';
import { Grid, SwipeableDrawer, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { getDateRange } from '@sda/utils/date';
import { getEventLocation, getRoundedFollowers, pluralize } from '@sda/utils/string';
import { useSelector } from '@client/hooks/redux.hooks';
import {
  getGroupedMapHits, getMapHitTotal, getMapLoaded, getMapStatus,
} from '@client/selectors/features/map';
import { ConnectCard } from '../../common/ConnectCard';
import { EmptyResults } from '../EmptyResults';
import { ConnectCardGroup } from '../ConnectCardGroup';
import { Puller } from './Puller';

// Height of drawer peeking out + height of BottomAppBar
const drawerBleed = 125;

const useStyles = makeStyles()((theme) => ({
  container: {
    marginTop: drawerBleed * -1,
    overflow: 'auto',
  },
  cardList: {
    backgroundColor: theme.palette.grey[50],
    padding: theme.spacing(0, 2, 2, 2),
    paddingBottom: `calc(${theme.spacing(2)} + ${theme.heights.BottomAppBar}px)`,
  },
  pullBleed: {
    position: 'static',
    backgroundColor: theme.palette.grey[50],
    borderTopLeftRadius: theme.radii.large,
    borderTopRightRadius: theme.radii.large,
    visibility: 'visible',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-start',
    padding: theme.spacing(1, 2, 2, 2),
  },
  pullBleedOpen: {
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
  },
  paper: {
    height: `calc(100% - ${drawerBleed * 2}px + 54px)`,
    overflow: 'visible',
    zIndex: theme.zIndex.appBar + 1,
  },
  modal: {
    bottom: theme.heights.BottomAppBar,
    top: theme.heights.MobileAppBar,
    zIndex: theme.zIndex.appBar + 1,
  },
  swipeArea: {
    zIndex: theme.zIndex.appBar + 2,
  },
  disabledSwipeArea: {
    display: 'none',
  },
  showCallCount: {
    marginTop: theme.spacing(1.5),
  },
  card: {
    '&:not(:last-of-type)': {
      marginBottom: theme.spacing(2),
    },
  },
}));

export const MobileDrawer = () => {
  const { classes, cx } = useStyles();
  const [open, setOpen] = useState(false);

  const handleDrawerOpen = useCallback(() => {
    setOpen(true);
  }, []);

  const handleDrawerClose = useCallback(() => {
    setOpen(false);
  }, []);

  const handleDrawerToggle = useCallback(() => {
    setOpen(!open);
  }, [open]);

  const nbHits = useSelector(getMapHitTotal);
  const hits = useSelector(getGroupedMapHits);

  const isLoaded = useSelector(getMapLoaded);
  const status = useSelector(getMapStatus);

  return (
    <SwipeableDrawer
      anchor="bottom"
      open={open}
      onClose={handleDrawerClose}
      onOpen={handleDrawerOpen}
      disableSwipeToOpen={false}
      swipeAreaWidth={drawerBleed}
      disableBackdropTransition
      disableDiscovery
      hideBackdrop
      keepMounted
      ModalProps={{
        className: classes.modal,
        keepMounted: true,
      }}
      PaperProps={{
        className: classes.paper,
      }}
      SwipeAreaProps={{
        className: cx(classes.swipeArea, open && classes.disabledSwipeArea),
        onClick: handleDrawerOpen,
      }}
    >
      <div className={classes.container}>
        <div className={cx(classes.pullBleed, open && classes.pullBleedOpen)}>
          <Puller onClick={handleDrawerToggle} />

          <Typography
            variant="body2"
            color="disabled"
            className={classes.showCallCount}
          >
            {pluralize('Show Call', nbHits)}
          </Typography>
        </div>

        {Boolean(isLoaded) && Boolean(status) && (
          <div className={classes.cardList}>
            {!nbHits ? <EmptyResults /> : hits.map((hitSet) => {
              if (hitSet.hits.length > 1) {
                return (
                  <div
                    key={hitSet.profileId}
                    className={classes.card}
                  >
                    <Grid
                      container
                      spacing={2}
                    >
                      <ConnectCardGroup
                        hitSet={hitSet.hits}
                      />
                    </Grid>
                  </div>
                );
              }

              const [hit] = hitSet.hits;

              return (
                <ConnectCard
                  containerClassName={classes.card}
                  key={hit.objectID}
                  imgSrc={hit.publicBeacon.profileData.publicImageUrl}
                  path={`/connect/${hit.objectID}`}
                  title={hit.publicBeacon.profileData.name}
                  location={getEventLocation(hit.publicBeacon)}
                  numReviews={hit.publicBeacon.profileData.numReviews}
                  score={hit.publicBeacon.profileData.score}
                  dateRange={getDateRange(hit.publicBeacon)}
                  isFeatured={hit.publicBeacon.isFeatured}
                  badge={hit.publicBeacon.profileData.badge}
                  spotifyMonthlyActiveListeners={getRoundedFollowers(hit.publicBeacon.profileData.spotifyMonthlyActiveListeners, 'compact')}
                />
              );
            })}
          </div>
        )}
      </div>
    </SwipeableDrawer>
  );
};
